/** CSPM enums — keep aligned with backend `cspm.constants.ts`. */

export const CSPM_SOURCE_TYPES = [
  'opportunity',
  'production_request',
  'sales_order',
  'contract_review',
  'epc_contract',
] as const;

export type CspmSourceType = (typeof CSPM_SOURCE_TYPES)[number];

export const CSPM_SOURCE_TYPE_LABELS: Record<CspmSourceType, string> = {
  opportunity: 'Opportunity',
  production_request: 'Production request',
  sales_order: 'Customer order',
  contract_review: 'Contract review',
  epc_contract: 'EPC contract',
};

/** Allocation / transfer target document types (Phase 1). */
export const CSPM_ALLOCATION_TARGET_TYPES = [
  'opportunity',
  'production_request',
  'sales_order',
  'contract_review',
  'epc_contract',
  'work_order',
  'project',
] as const;

export type CspmAllocationTargetType = (typeof CSPM_ALLOCATION_TARGET_TYPES)[number];

export const CSPM_ALLOCATION_TARGET_LABELS: Record<CspmAllocationTargetType, string> = {
  opportunity: 'Opportunity',
  production_request: 'Production request',
  sales_order: 'Customer order',
  contract_review: 'Contract review',
  epc_contract: 'EPC contract',
  work_order: 'Work order',
  project: 'Project',
};

export const CSPM_CLASSIFICATIONS = [
  'RAW_MATERIAL',
  'CONSUMABLE',
  'COMPONENT',
  'SPARE_PART',
  'INSTRUMENT',
  'EQUIPMENT',
  'TOOL',
  'MEASURING_EQUIPMENT',
  'SAMPLE',
  'PROTOTYPE',
  'DOCUMENT',
  'OTHER',
] as const;

export type CspmClassification = (typeof CSPM_CLASSIFICATIONS)[number];

export const CSPM_CLASSIFICATION_LABELS: Record<CspmClassification, string> = {
  RAW_MATERIAL: 'Raw material',
  CONSUMABLE: 'Consumable',
  COMPONENT: 'Component',
  SPARE_PART: 'Spare part',
  INSTRUMENT: 'Instrument',
  EQUIPMENT: 'Equipment',
  TOOL: 'Tool',
  MEASURING_EQUIPMENT: 'Measuring equipment',
  SAMPLE: 'Sample',
  PROTOTYPE: 'Prototype',
  DOCUMENT: 'Document',
  OTHER: 'Other',
};

export const CSPM_PURPOSES = [
  'FOR_CONSUMPTION',
  'FOR_INSTALLATION',
  'FOR_REPAIR_OR_SERVICE',
  'FOR_TESTING',
  'FOR_REFERENCE',
  'FOR_DELIVERY_WITH_ORDER',
  'TEMPORARY_CUSTODY',
] as const;

export type CspmPurpose = (typeof CSPM_PURPOSES)[number];

export const CSPM_PURPOSE_LABELS: Record<CspmPurpose, string> = {
  FOR_CONSUMPTION: 'For consumption',
  FOR_INSTALLATION: 'For installation',
  FOR_REPAIR_OR_SERVICE: 'For repair / service',
  FOR_TESTING: 'For testing',
  FOR_REFERENCE: 'For reference',
  FOR_DELIVERY_WITH_ORDER: 'For delivery with order',
  TEMPORARY_CUSTODY: 'Temporary custody',
};

export const CSPM_CONDITIONS = [
  'GOOD',
  'DAMAGED',
  'OPEN_PACKAGE',
  'CORRODED',
  'BROKEN',
  'MISSING_PARTS',
  'UNKNOWN',
  'OTHER',
] as const;

export type CspmCondition = (typeof CSPM_CONDITIONS)[number];

export const CSPM_CONDITION_LABELS: Record<CspmCondition, string> = {
  GOOD: 'Good',
  DAMAGED: 'Damaged',
  OPEN_PACKAGE: 'Open package',
  CORRODED: 'Corroded',
  BROKEN: 'Broken',
  MISSING_PARTS: 'Missing parts',
  UNKNOWN: 'Unknown',
  OTHER: 'Other',
};

export const CSPM_CRITICALITY = ['NON_CRITICAL', 'CRITICAL', 'HIGH_RISK'] as const;

export type CspmCriticality = (typeof CSPM_CRITICALITY)[number];

export const CSPM_CRITICALITY_LABELS: Record<CspmCriticality, string> = {
  NON_CRITICAL: 'Non-critical',
  CRITICAL: 'Critical',
  HIGH_RISK: 'High risk',
};

export const CSPM_EXCEPTION_TYPES = [
  'DAMAGED',
  'LOST',
  'DETERIORATED',
  'UNSUITABLE',
  'SHORT',
  'EXCESS',
  'NONCONFORMING',
  'MISSING_DOCS',
  'OTHER',
] as const;

export type CspmExceptionType = (typeof CSPM_EXCEPTION_TYPES)[number];

export const CSPM_EXCEPTION_TYPE_LABELS: Record<CspmExceptionType, string> = {
  DAMAGED: 'Damaged',
  LOST: 'Lost',
  DETERIORATED: 'Deteriorated',
  UNSUITABLE: 'Unsuitable',
  SHORT: 'Shortage',
  EXCESS: 'Excess',
  NONCONFORMING: 'Nonconforming',
  MISSING_DOCS: 'Missing documents',
  OTHER: 'Other',
};

export const CSPM_DISPOSITION_TYPES = [
  'RETURN_TO_CUSTOMER',
  'CUSTOMER_AUTHORIZED_DISPOSAL',
  'CUSTOMER_AUTHORIZED_RETENTION',
  'SCRAP_WITH_AUTHORIZATION',
  'ACCEPT_WITH_CONCESSION',
  'REPLACE_BY_CUSTOMER',
  'OTHER',
] as const;

export type CspmDispositionType = (typeof CSPM_DISPOSITION_TYPES)[number];

export const CSPM_DISPOSITION_TYPE_LABELS: Record<CspmDispositionType, string> = {
  RETURN_TO_CUSTOMER: 'Return to customer',
  CUSTOMER_AUTHORIZED_DISPOSAL: 'Customer-authorized disposal',
  CUSTOMER_AUTHORIZED_RETENTION: 'Customer-authorized retention',
  SCRAP_WITH_AUTHORIZATION: 'Scrap with authorization',
  ACCEPT_WITH_CONCESSION: 'Accept with concession',
  REPLACE_BY_CUSTOMER: 'Replace by customer',
  OTHER: 'Other',
};

export const CSPM_REQUIREMENT_STATUSES = ['Draft', 'Open', 'Closed', 'Cancelled'] as const;
export const CSPM_RECEIPT_STATUSES = ['Draft', 'Posted', 'Cancelled'] as const;
export const CSPM_RETURN_STATUSES = ['Draft', 'Posted', 'Cancelled'] as const;

export const CSPM_CUSTODY_STATUSES = [
  'NONE',
  'IDENTITY_CREATED',
  'IN_WORKSHOP',
  'READY_FOR_RETURN',
  'RETURNED',
] as const;

export type CspmCustodyStatus = (typeof CSPM_CUSTODY_STATUSES)[number];

export const CSPM_CUSTODY_STATUS_LABELS: Record<CspmCustodyStatus, string> = {
  NONE: 'None',
  IDENTITY_CREATED: 'Identity created',
  IN_WORKSHOP: 'In workshop',
  READY_FOR_RETURN: 'Ready for return',
  RETURNED: 'Returned',
};

export function enumOptions<T extends string>(
  values: readonly T[],
  labels: Record<T, string>
): Array<{ value: T; label: string }> {
  return values.map((value) => ({ value, label: labels[value] }));
}

export type CustomerPropertyRequirementLine = {
  id?: string;
  line_number?: number;
  classification?: string;
  purpose?: string;
  criticality?: string;
  item_id?: string | null;
  customer_item_ref?: string | null;
  description: string;
  expected_qty?: number;
  uom?: string;
  received_qty?: number;
  remarks?: string | null;
};

export type CustomerPropertyRequirement = {
  id: string;
  requirement_number: string;
  customer_id: string;
  customer_name?: string;
  source_document_type: string;
  source_document_id: string;
  status: string;
  remarks?: string | null;
  lines?: CustomerPropertyRequirementLine[];
  created_at?: string;
  updated_at?: string;
};

export type CustomerPropertyReceiptLine = {
  id?: string;
  line_number?: number;
  requirement_line_id?: string | null;
  classification?: string;
  purpose?: string;
  criticality?: string;
  item_id?: string | null;
  customer_item_ref?: string | null;
  description: string;
  uom?: string;
  expected_qty?: number;
  received_qty?: number;
  damaged_qty?: number;
  accepted_qty?: number;
  rejected_qty?: number;
  short_qty?: number;
  excess_qty?: number;
  condition_code?: string;
  condition_notes?: string | null;
  manufacturer?: string | null;
  model?: string | null;
  serial_number?: string | null;
  batch_number?: string | null;
  lot_number?: string | null;
  heat_number?: string | null;
  dimension?: string | null;
  storage_requirement?: string | null;
  location_id?: string | null;
  expiry_date?: string | null;
  declared_value?: number | null;
  reference_value?: number | null;
  insurance_value?: number | null;
  posts_to_inventory?: boolean;
  quality_status?: string;
  remarks?: string | null;
  asset_id?: string | null;
  maintenance_case_id?: string | null;
  custody_status?: string;
};

export type CustomerPropertyReceipt = {
  id: string;
  receipt_number: string;
  customer_id: string;
  customer_name?: string;
  source_document_type: string;
  source_document_id: string;
  requirement_id?: string | null;
  is_unplanned?: boolean;
  unplanned_reason?: string | null;
  customer_delivery_note?: string | null;
  packing_list_ref?: string | null;
  customer_reference?: string | null;
  receipt_datetime?: string;
  warehouse_id: string;
  project_id?: string | null;
  vehicle_courier_ref?: string | null;
  received_by?: string | null;
  remarks?: string | null;
  status: string;
  inspection_required?: boolean;
  inspection_status?: string | null;
  inspection_record_id?: string | null;
  posted_at?: string | null;
  lines?: CustomerPropertyReceiptLine[];
  created_at?: string;
  updated_at?: string;
};

export type CustomerPropertyAllocation = {
  id: string;
  receipt_id: string;
  receipt_line_id: string;
  quantity: number;
  uom?: string;
  target_document_type: string;
  target_document_id: string;
  status: string;
  remarks?: string | null;
  created_at?: string;
};

export type CustomerPropertyException = {
  id: string;
  exception_number?: string;
  receipt_id?: string | null;
  receipt_line_id?: string | null;
  customer_id: string;
  exception_type?: string;
  status: string;
  description?: string | null;
  quantity?: number | null;
  ncr_id?: string | null;
  notification_ref?: string | null;
  created_at?: string;
};

export type CustomerPropertyReturnLine = {
  id?: string;
  receipt_line_id?: string | null;
  allocation_id?: string | null;
  item_id?: string | null;
  description: string;
  quantity: number;
  uom?: string;
  serial_number?: string | null;
  heat_number?: string | null;
  lot_number?: string | null;
  batch_number?: string | null;
  condition_code?: string | null;
  condition_notes?: string | null;
  remarks?: string | null;
};

export type CustomerPropertyReturn = {
  id: string;
  return_number: string;
  customer_id: string;
  customer_name?: string;
  receipt_id?: string | null;
  source_document_type?: string | null;
  source_document_id?: string | null;
  return_datetime?: string;
  warehouse_id?: string | null;
  reason?: string | null;
  remarks?: string | null;
  status: string;
  customer_ack_name?: string | null;
  customer_ack_at?: string | null;
  acknowledgement_token?: string | null;
  acknowledgement_signature_document_id?: string | null;
  acknowledgement_notes?: string | null;
  lines?: CustomerPropertyReturnLine[];
  created_at?: string;
};

export type ReconciliationPreview = {
  customer_id: string;
  source_document_type: string;
  source_document_id: string;
  expected_qty: number;
  received_qty: number;
  accepted_qty: number;
  allocated_qty: number;
  returned_qty: number;
  remaining_qty: number;
  unresolved_qty: number;
  lines: Array<{
    description: string;
    expected_qty: number;
    received_qty: number;
    accepted_qty: number;
    allocated_qty: number;
    returned_qty: number;
    remaining_qty: number;
  }>;
};

export type CustomerPropertyReconciliation = {
  id: string;
  reconciliation_number: string;
  customer_id: string;
  source_document_type: string;
  source_document_id: string;
  status: string;
  snapshot_json?: ReconciliationPreview | Record<string, unknown>;
  unresolved_qty?: number;
  variance_approved?: boolean;
  variance_reason?: string | null;
  closed_at?: string | null;
};
